import { SyntheticEvent, useCallback, useMemo } from "react"
import {
  BannerInfo,
  Block,
  Button,
  HeadingMedium,
  Input,
  LabelLarge,
  ParagraphMedium,
  StatefulButtonGroup,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { SlideMenuProps } from "#/shared/components/SlideMenu"
import { SWAP_SLIPPAGE_DEFAULT_VALUE } from "#/shared/constants"
import { useSwapSlippageInput } from "#/shared/hooks"

import SwapModalFormSettingsWrapper from "./SwapModalFormSettingsWrapper"

export type SwapModalSlippageProps = Pick<
  SlideMenuProps,
  "isOpen" | "onClose"
> & {
  slippage: string
  setSlippage: (
    value: string,
    shouldValidate?: boolean,
  ) => Promise<string | void>
}

const PRESET_SLIPPAGE_VALUES = ["0.1", "0.5", "1"]

export default function SwapModalSlippage({
  slippage,
  setSlippage,
  onClose,
  isOpen,
}: SwapModalSlippageProps) {
  const [css, theme] = useStyletron()

  const { onInputChange, error } = useSwapSlippageInput(setSlippage)

  const isButtonDisabled = useMemo(
    () => slippage === "" || !!error,
    [slippage, error],
  )

  const closeSlippageSettingsHandler = useCallback(
    (e: SyntheticEvent<HTMLButtonElement>) => {
      // Reset to default value when clicking back button
      setSlippage(SWAP_SLIPPAGE_DEFAULT_VALUE.toString())
      onClose(e)
    },
    [onClose, setSlippage],
  )

  return (
    <SwapModalFormSettingsWrapper
      isOpen={isOpen}
      onClose={closeSlippageSettingsHandler}
      title="Settings"
    >
      <Block flex={1}>
        <HeadingMedium
          marginTop={theme.sizing.scale800}
          marginBottom={theme.sizing.scale900}
        >
          Slippage
        </HeadingMedium>
        <BannerInfo
          artworkType="icon"
          overrides={{ Root: { style: () => ({ marginInline: 0 }) } }}
        >
          Slippage is the difference between quoted <br />
          and final price.
        </BannerInfo>
        <Block marginBottom={theme.sizing.scale700}>
          <Block
            className={css({
              margin: `${theme.sizing.scale700} 0`,
              display: "flex",
              gap: theme.sizing.scale300,
            })}
          >
            <StatefulButtonGroup
              stateReducer={(_, nextState, currentState) =>
                nextState.selected.length === 0 ? currentState : nextState
              }
              initialState={{ selected: 0 }}
              mode="radio"
              shape="pill"
              size="medium"
            >
              {PRESET_SLIPPAGE_VALUES.map((value) => (
                <Button
                  width="72px"
                  onClick={() => setSlippage(value)}
                  type="button"
                  overrides={{
                    BaseButton: {
                      style: () => ({
                        background:
                          slippage === value
                            ? theme.colors.black
                            : theme.colors.backgroundSecondary,
                        color:
                          slippage === value
                            ? theme.colors.white
                            : theme.colors.black,
                      }),
                      props: { "aria-checked": slippage === value },
                    },
                  }}
                >
                  {value}%
                </Button>
              ))}
            </StatefulButtonGroup>
            <Input
              value={slippage}
              onChange={onInputChange}
              endEnhancer={() => <LabelLarge>%</LabelLarge>}
              inputMode="decimal"
              type="text"
              error={!!error}
              overrides={{
                Root: {
                  style: () => ({ borderRadius: theme.borders.radius500 }),
                },
              }}
            />
          </Block>
          <ParagraphMedium color={theme.colors.negative}>
            {error}
          </ParagraphMedium>
        </Block>
      </Block>
      <Button
        size="large"
        width="100%"
        onClick={onClose}
        disabled={isButtonDisabled}
        type="button"
      >
        Done
      </Button>
    </SwapModalFormSettingsWrapper>
  )
}
